// The Token classes table (SCR-584, REQ-233): the Usage screen's panel under the summary tiles,
// and the same five-row table inside each breakdown and explorer row's detail. Tokens always; the
// AWS list cost and Price columns only when the line carries that figure's split (classes-model.ts)
// — a hidden figure has no column at all, never a column of "$0". A line that leaves an unpriced
// model's usage out marks its total with the partial-money marker and says which models.
import type { ApiError } from "../../api/errors";
import { Card } from "../../primitives/page";
import { RowDetail, type RowFact } from "../../primitives/row-detail";
import { Empty, ErrorBanner, Loading } from "../../primitives/states";
import { DataTable, Money, type Column } from "../../primitives/table";
import { classTable, type ClassTable, type ClassTableRow } from "./classes-model";
import { formatCount, unpricedTitle, type ClassLine } from "./figures";
import { PartialMoney } from "./partial-money";

const EMPTY_MESSAGE = "No usage in this window.";

function moneyCell(micro: number | null, row: ClassTableRow, table: ClassTable) {
  if (row.key === "total") return <PartialMoney micro={micro} unpricedModels={table.unpricedModels} />;
  return micro === null ? "—" : <Money micro={micro} />;
}

function classColumns(table: ClassTable): Column<ClassTableRow>[] {
  const label = (row: ClassTableRow) => (row.key === "total" ? <strong>{row.label}</strong> : row.label);
  const columns: Column<ClassTableRow>[] = [
    { key: "class", header: "Token class", help: "usage.classes", cell: label },
    { key: "tokens", header: "Tokens", numeric: true, cell: (row) => formatCount(row.tokens) },
  ];
  if (table.showCost) columns.push({ key: "cost", header: "AWS list cost", numeric: true, help: "common.awsCost", cell: (row) => moneyCell(row.cost, row, table) });
  if (table.showPrice) columns.push({ key: "price", header: "Price", numeric: true, help: "usage.price", cell: (row) => moneyCell(row.price, row, table) });
  return columns;
}

/** One line's Token classes table: five classes and the total, money as the line allows; the empty state with no tokens. */
export function ClassesTable({ line, caption }: { line: ClassLine; caption: string }) {
  const table = classTable(line);
  if (table.empty) return <Empty message={EMPTY_MESSAGE} />;
  const note = unpricedTitle(table.unpricedModels);
  return (
    <>
      <DataTable caption={caption} hideCaption columns={classColumns(table)} rows={[...table.rows, table.total]} rowKey={(row) => row.key} emptyMessage={EMPTY_MESSAGE} />
      {note && (table.showCost || table.showPrice) && <p class="card-note">{note}.</p>}
    </>
  );
}

export interface TokenClassesPanelProps {
  /** The scope's totals under the viewer's money policy; null while they load. */
  line: ClassLine | null;
  error?: ApiError | null | undefined;
  onRetry?: (() => void) | undefined;
  /** What the totals cover, for the table's caption ("the last 30 days"). */
  span: string;
}

/** The Token classes panel under the summary tiles: loading, error, empty, or the table. */
export function TokenClassesPanel({ line, error, onRetry, span }: TokenClassesPanelProps) {
  return (
    <Card title="Token classes" titleId="usage-classes" help="usage.classes">
      {error ? (
        <ErrorBanner error={error} {...(onRetry ? { onRetry } : {})} />
      ) : !line ? (
        <Loading label="Loading token classes…" />
      ) : (
        <ClassesTable line={line} caption={`Tokens and money by token class, ${span}`} />
      )}
    </Card>
  );
}

export interface LineDetailProps {
  /** The line's name: a model's display name, a team's or a person's name — never an id. */
  title: string;
  line: ClassLine;
  /** The facts above the table (requests, tokens, fallbacks…). */
  facts: readonly RowFact[];
  onClose(): void;
}

/** A breakdown or explorer row's detail: its facts and its own Token classes table. */
export function LineDetail({ title, line, facts, onClose }: LineDetailProps) {
  return (
    <RowDetail title={title} open onClose={onClose} facts={facts} class="dialog-wide">
      <h3 class="usage-share-title">Token classes</h3>
      <ClassesTable line={line} caption={`Tokens and money by token class for ${title}`} />
    </RowDetail>
  );
}
